import React, { useState } from 'react'
import { Box, Button, Slider, TextField } from '@mui/material'
import { clampSampleSize, sampleSizeOptions } from '../../utils/themeAIDistribution'

interface SampleSizePickerProps {
  poolSize: number
  value: number
  onChange: (size: number) => void
  /** A quick pick. Defaults to onChange; the empty test view starts the test instead. */
  onQuickPick?: (size: number) => void
  disabled?: boolean
}

// How many responses a test codes. The quick picks stay one click, and the
// slider and the box reach any size up to the pool (Matt, 23 Sep: "you'll
// want to have a little scale to go actually I want to test it on 500
// because I could have 10,000, so you should have a sliding scale").
const SampleSizePicker: React.FC<SampleSizePickerProps> = ({ poolSize, value, onChange, onQuickPick, disabled }) => {
  // What the user is typing, kept until they leave the box so "5" on the way
  // to "500" is not clamped under them. Undefined shows the value.
  const [draft, setDraft] = useState<string>()
  // Where the slider sits while it is dragged. The page hears the size once,
  // on release, not on every pixel. Undefined shows the value.
  const [sliding, setSliding] = useState<number>()
  const size = clampSampleSize(value, poolSize)
  const shown = sliding ?? size
  const quickPicks = sampleSizeOptions(poolSize)

  if (poolSize <= 1) return null

  const sliderValue = (next: number | number[]) => clampSampleSize(Array.isArray(next) ? next[0] : next, poolSize)

  const handleSlideCommitted = (next: number | number[]) => {
    setSliding(undefined)
    onChange(sliderValue(next))
  }

  // The page hears a typed size once, on blur or Enter. An empty or
  // non-numeric box keeps the size it had.
  const handleCommitTyped = () => {
    const parsed = Number(draft)
    if (draft !== undefined && draft.trim() !== '' && Number.isFinite(parsed)) onChange(clampSampleSize(parsed, poolSize))
    setDraft(undefined)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 640 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {quickPicks.map((pick) => (
          <Button
            key={pick}
            variant={pick === size ? 'contained' : 'outlined'}
            color="info"
            disabled={disabled}
            onClick={() => (onQuickPick ?? onChange)(pick)}
            sx={{ minWidth: 120 }}
          >
            {pick === poolSize ? `All ${pick.toLocaleString()}` : `${pick.toLocaleString()} responses`}
          </Button>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Slider
          color="info"
          size="small"
          min={1}
          max={poolSize}
          value={shown}
          disabled={disabled}
          onChange={(_, next) => setSliding(sliderValue(next))}
          onChangeCommitted={(_, next) => handleSlideCommitted(next)}
          marks={quickPicks.filter((pick) => pick < poolSize).map((pick) => ({ value: pick }))}
          aria-label="Sample size slider"
          sx={{ flex: 1, minWidth: 160 }}
        />
        <TextField
          size="small"
          type="number"
          value={draft ?? String(shown)}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={handleCommitTyped}
          onKeyDown={(event) => event.key === 'Enter' && handleCommitTyped()}
          slotProps={{ htmlInput: { min: 1, max: poolSize, 'aria-label': 'Sample size' } }}
          helperText={`of ${poolSize.toLocaleString()}`}
          sx={{ width: 120, flexShrink: 0 }}
        />
      </Box>
    </Box>
  )
}

export default SampleSizePicker
